import { AttachmentSelectionSource } from 'owa-attachment-data';
import {
    AttachmentModel,
    lazyGetAttachment,
    ReferenceAttachmentModel,
} from 'owa-attachment-model-store';
import { SaveToCloudStatus } from 'owa-attachment-savetocloud';
import { getErrorMessageFromWacAttachmentStatus } from 'owa-attachment-wac';
import getWacAttachmentInfo from 'owa-attachment-wac/lib/services/getWacAttachmentInfo';
import getWacInfoForOneDriveJsApi from 'owa-attachment-wac/lib/services/getWacInfoForOneDriveJsApi';
import { postToWac } from 'owa-attachment-wac/lib/utils/iframeUtilities';
import { ClientAttachmentId, ClientItemId } from 'owa-client-ids';
import { isFeatureEnabled } from 'owa-feature-flags';
import { getOfficeOnlineAppFromExtension, OfficeOnlineApp } from 'owa-file';
import { getGuid } from 'owa-guid';
import loc from 'owa-localize';
import deleteItemService from 'owa-mail-store/lib/services/deleteItemService';
import AttachmentDataProviderType from 'owa-service/lib/contract/AttachmentDataProviderType';
import WacAttachmentStatus from 'owa-service/lib/contract/WacAttachmentStatus';
import WacAttachmentType from 'owa-service/lib/contract/WacAttachmentType';
import { lazySetPreviewError, lazySetPreviewLoading, lazySetWacPreviewState } from 'owa-sxsdata';
import { orchestrator } from 'satcheljs';
import {
    onAttachmentIdUpdate,
    onEditModeChange,
    setSaveToCloudStatus,
} from '../actions/internalActions';
import * as publicActions from '../actions/publicLazyActions';
import synchronizeWacAttachment from '../services/synchronizeWacAttachment';
import { EditMode } from '../store/schema/SxSEditState';
import { SxSReadingPaneMode } from '../store/schema/SxSReadingPaneState';
import getOrCreateSxSStoreData from '../store/Store';
import { EnteredEditModeEvent } from '../utils/eventNames';
import logWacDatapoint, { WacDatapointType } from '../utils/logWacDatapoint';
import {
    EditAndReplyErrorText,
    EditingInTheCloudMessage,
    MakingACopyMessage,
} from './editWacDocumentOrchestrators.locstring.json';
import { getMailboxRequestOptions } from 'owa-request-options-types';
import { ComposeOperation } from 'owa-mail-compose-store';

async function editAndReplyOrchestrator(
    sxsId: string,
    clickTime: Date,
    loadDraftToCompose: (
        itemId: ClientItemId,
        forceInsertSignatureOperation: ComposeOperation
    ) => Promise<void>,
    createReply: (referenceItemId: ClientItemId) => Promise<ClientItemId>,
    trySaveAndCloseCompose: (conversationId: string, composeId: string) => Promise<ClientItemId>
) {
    const sxsStore = getOrCreateSxSStoreData(sxsId);
    // Hiding Save to OneDrive button for now and in WI 62202 we will discuss the cost of and implement the feature to show Upload to OneDrive button if needed
    setSaveToCloudStatus(
        SaveToCloudStatus.disabled,
        sxsStore.extendedViewState.attachmentId,
        sxsId
    );
    logWacDatapoint(
        sxsStore.extendedViewState.attachmentId,
        WacDatapointType.WacEditAndReply,
        AttachmentSelectionSource[sxsStore.extendedViewState.selectionSource]
    );

    const setPreviewLoading = await lazySetPreviewLoading.import();
    setPreviewLoading(
        sxsStore.viewState,
        sxsStore.extendedViewState.isCloudy
            ? loc(EditingInTheCloudMessage)
            : loc(MakingACopyMessage)
    );
    onEditModeChange(EditMode.EnteringEdit, sxsStore.editState.itemId, sxsId);

    let createdDraftId: ClientItemId | null = null;
    let reuseDraftId: boolean = true;
    try {
        // If the user hits "Reply" or ""Reply all" before they hit "Edit and reply", we try to re-use the draft instead of creating a new draft
        if (sxsStore.readingPaneState?.mode === SxSReadingPaneMode.ConversationReadingPane) {
            const conversationId = sxsStore.readingPaneState.conversationId;
            createdDraftId = await trySaveAndCloseCompose(conversationId.Id, sxsStore.composeId);
        }

        if (!createdDraftId) {
            reuseDraftId = false;
            createdDraftId = await createReply(sxsStore.editState.itemId);
        }
    } catch (error) {
        const setPreviewError = await lazySetPreviewError.import();
        setPreviewError(sxsStore.viewState, loc(EditAndReplyErrorText));
        onEditModeChange(EditMode.View, sxsStore.editState.itemId, sxsId);
    }

    if (createdDraftId) {
        await getWacAttachmentInfoAndChangeToEditMode(
            sxsId,
            createdDraftId,
            sxsStore.extendedViewState.attachmentId,
            true, // copyAttachment
            clickTime,
            loadDraftToCompose,
            !reuseDraftId ? ComposeOperation.Reply : undefined,
            () => {
                if (!reuseDraftId) {
                    deleteDraftItem(createdDraftId);
                }
            }
        );
    }

    sxsStore.eventManager.emit(EnteredEditModeEvent, sxsStore.editState.mode === EditMode.Edit);
}

async function editInDraftOrchestrator(
    sxsId: string,
    clickTime: Date,
    loadDraftToCompose: (
        itemId: ClientItemId,
        forceInsertSignatureOperation: ComposeOperation
    ) => Promise<void>
) {
    const sxsStore = getOrCreateSxSStoreData(sxsId);
    logWacDatapoint(
        sxsStore.extendedViewState.attachmentId,
        WacDatapointType.WacEdit,
        AttachmentSelectionSource[sxsStore.extendedViewState.selectionSource]
    );

    const setPreviewLoading = await lazySetPreviewLoading.import();
    setPreviewLoading(sxsStore.viewState, '');
    onEditModeChange(EditMode.EnteringEdit, sxsStore.editState.itemId, sxsId);

    await getWacAttachmentInfoAndChangeToEditMode(
        sxsId,
        sxsStore.editState.itemId,
        sxsStore.extendedViewState.attachmentId,
        false, // copyAttachment
        clickTime,
        loadDraftToCompose,
        null, // forceInsertSignatureOperation
        null // onFailure
    );

    sxsStore.eventManager.emit(EnteredEditModeEvent, sxsStore.editState.mode === EditMode.Edit);
}

async function getWacAttachmentInfoAndChangeToEditMode(
    sxsId: string,
    draftId: ClientItemId,
    attachmentId: ClientAttachmentId,
    copyAttachment: boolean,
    clickTime: Date,
    loadDraftToCompose: (
        itemId: ClientItemId,
        forceInsertSignatureOperation: ComposeOperation
    ) => Promise<void>,
    forceInsertSignatureOperation: ComposeOperation,
    onFailure: () => void
) {
    const sxsStore = getOrCreateSxSStoreData(sxsId);
    let response: WacAttachmentType;
    try {
        response = await getWacAttachmentInfoWithConversionScenarioHandling(
            attachmentId,
            copyAttachment ? draftId : null,
            AttachmentSelectionSource[sxsStore.extendedViewState.selectionSource]
        );
        if (response.Status !== WacAttachmentStatus.Success) {
            handleWacFailure(sxsId, onFailure, response.Status);
            return;
        }
    } catch (error) {
        handleWacFailure(sxsId, onFailure, WacAttachmentStatus.UnknownError);
        return;
    }

    if (copyAttachment) {
        // update attachment ID
        onAttachmentIdUpdate(
            {
                ...response.AttachmentId,
                mailboxInfo: null,
            },
            sxsId
        );
    }

    let useJsApi: boolean = false;
    let openUrl: string;
    let fileSize: number;
    if (isFeatureEnabled('doc-SxS-jsApi-ODB-Readonly')) {
        const getAttachment = await lazyGetAttachment.import();
        const attachmentModel: AttachmentModel = getAttachment(attachmentId);
        if (attachmentModel) {
            const fileName: string = attachmentModel.model.Name;
            const referenceAttachmentModel: ReferenceAttachmentModel = attachmentModel as ReferenceAttachmentModel;
            openUrl = isFeatureEnabled('doc-unwrap-safelinks')
                ? referenceAttachmentModel.originalUrl
                : referenceAttachmentModel.openUrl;
            fileSize = attachmentModel.model.Size;
            if (openUrl && fileName) {
                const extension = fileName.substring(fileName.lastIndexOf('.'));
                const officeOnlineApp: OfficeOnlineApp = getOfficeOnlineAppFromExtension(extension);
                useJsApi =
                    officeOnlineApp == OfficeOnlineApp.Word ||
                    officeOnlineApp == OfficeOnlineApp.Excel;
            }
        }
    }

    // set WAC preview state using the edit WAC URL
    const setWacPreviewState = await lazySetWacPreviewState.import();

    if (useJsApi) {
        const apiResponse = await getWacInfoForOneDriveJsApi(
            AttachmentDataProviderType.OneDrivePro,
            openUrl,
            true /* From Readonly to Edit mode */,
            true /* This is triggered by user clicking. Set redeemSharingLinkIfNecessary to true */
        );

        if (!apiResponse) {
            handleWacFailure(sxsId, onFailure, WacAttachmentStatus.UnknownError);
            return;
        }

        setWacPreviewState(
            sxsStore.viewState,
            apiResponse.openWith.wac.applicationUrl, // wacUrl
            {
                bootScriptUrl: apiResponse.openWith.wac.bootstrapperUrl,
                wopiSrcUrl: apiResponse.openWith.wac.wopiSrc,
                accessToken: apiResponse.openWith.wac.accessToken,
                accessTokenExpiry: apiResponse.openWith.wac.accessTokenExpiry,
                fileSize: fileSize,
                fileGetUrl: apiResponse.openWith.wac.fileGetUrl,
                bundleInfo: apiResponse.officeBundle,
                fetchStartTime: apiResponse.fetchStartTime,
                responseStartTime: apiResponse.responseStartTime,
                openUrl: openUrl,
                owaSessionId: getGuid(),
                userId: apiResponse.openWith.wac.userId,
                clientThrottlingProtection: apiResponse.openWith.wac.clientThrottlingProtection,
                requestedCallThrottling: apiResponse.openWith.wac.requestedCallThrottling,
            },
            null, // accessToken,
            null, // accessTokenTtl,
            null, // additionalParameters,
            null, // onedrive token
            clickTime.getTime(),
            apiResponse.currentUserRole.readOnly, // readOnly info
            sxsStore.viewState.previewPane
        );
    } else {
        setWacPreviewState(
            sxsStore.viewState,
            response.WacUrl,
            null, // JsApiInfo
            response.AccessToken,
            response.AccessTokenTtl,
            response.AdditionalParameters,
            response.Token, // contains ODC token and additional parameters (deprecated)
            clickTime.getTime(),
            null, // readOnly info
            sxsStore.viewState.previewPane
        );
    }

    onEditModeChange(EditMode.Edit, draftId, sxsId);

    if (!sxsStore.composeId) {
        loadDraftToCompose(draftId, forceInsertSignatureOperation);
    }
}

async function getWacAttachmentInfoWithConversionScenarioHandling(
    attachmentId: ClientAttachmentId,
    draftId: ClientItemId | null,
    actionSource: string
): Promise<WacAttachmentType> {
    let response = await getWacAttachmentInfo(
        attachmentId.Id,
        true,
        draftId ? draftId.Id : null,
        getMailboxRequestOptions(attachmentId.mailboxInfo),
        actionSource
    );

    if (response?.IsConversion && response.Status === WacAttachmentStatus.Success) {
        // create iframe
        const iframeElement = document.createElement('iframe');

        try {
            iframeElement.style.borderWidth = '0';
            iframeElement.style.position = 'absolute';
            iframeElement.style.width = iframeElement.style.height = '1px';
            document.body.appendChild(iframeElement);
            postToWac(iframeElement, response.WacUrl, response.Token);

            await synchronizeWacAttachment(
                response.AttachmentId.Id,
                getMailboxRequestOptions(attachmentId.mailboxInfo)
            );
        } finally {
            document.body.removeChild(iframeElement);
        }

        response = await getWacAttachmentInfoWithConversionScenarioHandling(
            { ...response.AttachmentId, mailboxInfo: attachmentId.mailboxInfo },
            null,
            actionSource
        );
    }

    return response;
}

async function handleWacFailure(
    sxsId: string,
    onFailure: () => void,
    responseStatus: WacAttachmentStatus
) {
    const sxsStore = getOrCreateSxSStoreData(sxsId);
    onFailure?.();
    const setPreviewError = await lazySetPreviewError.import();
    setPreviewError(sxsStore.viewState, getErrorMessageFromWacAttachmentStatus(responseStatus));
    onEditModeChange(EditMode.View, sxsStore.editState.itemId, sxsId);
}

function deleteDraftItem(draftId: ClientItemId) {
    if (draftId) {
        deleteItemService([draftId], 'HardDelete', getMailboxRequestOptions(draftId.mailboxInfo));
    }
}

orchestrator(publicActions.editAndReply, actionMessage => {
    const { loadDraftToCompose, createReply, trySaveAndCloseCompose, sxsId } = actionMessage;
    editAndReplyOrchestrator(
        sxsId,
        new Date(),
        loadDraftToCompose,
        createReply,
        trySaveAndCloseCompose
    );
});

orchestrator(publicActions.editInDraft, actionMessage => {
    const { loadDraftToCompose, sxsId } = actionMessage;
    editInDraftOrchestrator(sxsId, new Date(), loadDraftToCompose);
});

// export for unit testing
export { editAndReplyOrchestrator, editInDraftOrchestrator };
